/**
 * PlayerMatchReportsSectionContainer — wiring for the player-page section
 * (SE-6986).
 *
 * Owns the expansion state and the navigation into a full report. Designed to
 * be handed to the existing reports screen as a list header, so the section
 * sits ABOVE the single-player scout reports without touching their list.
 * @module features/player-match-reports/components/organisms/PlayerMatchReportsSection/PlayerMatchReportsSectionContainer
 */
import { router } from "expo-router";
import React, { useCallback, useState } from "react";

import AddToPipelineDialog from "@/features/player-kanban/components/organisms/AddToPipelineDialog";

import usePlayerMatchReports from "../../../hooks/usePlayerMatchReports";

import PlayerMatchReportsSectionView from "./PlayerMatchReportsSectionView";

/** Props for PlayerMatchReportsSectionContainer. */
export interface PlayerMatchReportsSectionContainerProps {
  /** The player whose page this is. */
  readonly playerId: string | undefined;
  /** Whether the scout-reports list below the header has rows. */
  readonly hasScoutReports: boolean;
  /**
   * Player display name — the promote dialog's subtitle (SE-7214) and the
   * subject of the expanded panel's scope callout (SE-8016).
   */
  readonly playerName?: string;
}

/**
 * Renders the match-reports section, wired.
 * @param root0 - Component props
 * @param root0.playerId - The player whose page this is
 * @param root0.hasScoutReports - Whether the list below has rows
 * @param root0.playerName - Player display name for the promote dialog
 * @returns The section element.
 */
const PlayerMatchReportsSectionContainer = ({
  playerId,
  hasScoutReports,
  playerName,
}: PlayerMatchReportsSectionContainerProps) => {
  const reports = usePlayerMatchReports(playerId);

  const [expandedMatchIds, setExpandedMatchIds] = useState<readonly string[]>(
    []
  );

  const [isPromoteOpen, setIsPromoteOpen] = useState(false);

  const onToggleRow = useCallback((matchId: string) => {
    setExpandedMatchIds(current =>
      current.includes(matchId)
        ? current.filter(id => id !== matchId)
        : [...current, matchId]
    );
  }, []);

  // Into the submitted report view (SE-6985), which lands the reader on the
  // whole report — the sanctioned place to see the other players, which this
  // section never renders.
  const onOpenReport = useCallback(
    (reportId: string) => router.push(`/match-reports/${reportId}`),
    []
  );

  const onPromote = useCallback(() => setIsPromoteOpen(true), []);
  const onClosePromote = useCallback(() => setIsPromoteOpen(false), []);

  // A whitespace-only id is truthy but just as unusable as an absent one, and
  // the dialog's add mutation would fire against it. Both the trigger and the
  // dialog hang off this single readiness check (mirrors the same guard on the
  // player query in `reports/screens/Main`).
  const canPromote = Boolean(playerId?.trim());

  /*
    The section ALWAYS renders (SE-8149).

    SE-7970 gave this container a `hideWhenEmpty` flag so the Reports & Notes
    tab could drop the section for a player with no match reports, on the
    reasoning that its empty sentence — right on `/players/[id]/reports`, whose
    whole subject it is — was wrong beside another empty state. SE-8149
    reversed that: every neighbour on the tab renders its own zero state, and
    the one section that vanished read as a rendering fault rather than as the
    scouting fact "nobody has watched this player under the new format".

    The flag is REMOVED rather than defaulted off. The tab was its only caller,
    so it would otherwise be an unreachable switch whose sole purpose was the
    behaviour just reversed — and a live one for anyone who finds it and
    assumes the reversal was an oversight.

    Its guard is worth recording even though it is gone, because the reasoning
    outlives it: the section could be withheld only once the answer was KNOWN
    to be "none", never on `rows.length === 0` alone, since that is equally
    what loading and a failed query look like. Hiding on either would have
    dropped a section the player may well have — absence of evidence rendered
    as evidence of absence. Nothing hides now, so the trap is closed by
    construction rather than by a condition.
  */

  return (
    <>
      <PlayerMatchReportsSectionView
        rows={reports.rows}
        stats={reports.stats}
        expandedMatchIds={expandedMatchIds}
        onToggleRow={onToggleRow}
        onOpenReport={onOpenReport}
        isLoading={reports.isLoading}
        error={reports.error}
        hasMore={reports.hasMore}
        hasScoutReports={hasScoutReports}
        playerName={playerName}
        onPromote={canPromote ? onPromote : undefined}
      />
      {/* SE-7214 promote-to-deep-dive: the EXISTING pick-pipeline-and-stage
          dialog with this player pre-selected, opened board-agnostic (no
          `boardId`) so the manager chooses both pipeline and stage. Deliberately
          not an auto-move — see the View's header-row comment.

          key flips on open so the dialog remounts with a fresh board/stage
          selection each time (key-reset — see PlayerHeroCtaCluster). */}
      {canPromote && playerId ? (
        <AddToPipelineDialog
          key={isPromoteOpen ? "atp-open" : "atp-closed"}
          isOpen={isPromoteOpen}
          onClose={onClosePromote}
          playerId={playerId}
          playerName={playerName}
        />
      ) : null}
    </>
  );
};

PlayerMatchReportsSectionContainer.displayName =
  "PlayerMatchReportsSectionContainer";

export default PlayerMatchReportsSectionContainer;
